import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import { Box } from 'styles/layout';
import FormWrapper from 'shared/components/forms/FormWrapper/FormWrapper';
import InputField from 'shared/components/forms/InputField/InputField';
import DatePicker from 'shared/components/forms/DatePicker/DatePicker';
import Button from 'shared/components/buttons/Button/Button';
import { useFormState } from 'shared/hooks';

import { schema, mapForm2Api, FormFields } from './utils';

const Hl7VisitForm = ({
  onSubmit,
  onCancel,
  onAddInsurance = null,
  initValues = null,
  disabled = false,
  hideButtons = false,
}) => {
  const {
    register,
    control,
    handleSubmit,
    getFieldError,
    setFormValues,
    canSubmitForm,
  } = useFormState(schema, { stateSlice: 'admin', showAsSnackbar: true });

  const visitId = initValues?.id;
  const showAddInsurance =
    !!visitId &&
    !!onAddInsurance &&
    initValues?.[FormFields.hasInsurance] !== 'Yes';

  useEffect(() => {
    if (!initValues) return;
    setFormValues(initValues);
  }, [initValues, setFormValues]);

  const onFormSubmit = React.useCallback(
    (submitData) => {
      onSubmit(mapForm2Api(submitData));
    },
    [onSubmit],
  );

  return (
    <FormWrapper
      formId="hl7VisitForm"
      hideButtons={hideButtons}
      onSubmit={handleSubmit(onFormSubmit)}
      onCancel={() => {
        setFormValues();
        onCancel();
      }}
      formDisabled={disabled}
      isSubmitDisabled={!canSubmitForm}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.patientAccountNumber)}
            label="Patient Account Number"
            required
            disabled={disabled}
            error={getFieldError(FormFields.patientAccountNumber)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <DatePicker
              control={control}
              name={FormFields.dischargeTimestamp}
              label="Discharge Date"
              disabled={disabled}
              error={getFieldError(FormFields.dischargeTimestamp)}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <DatePicker
              control={control}
              name={FormFields.admitTimestamp}
              label="Admit Date"
              disabled={disabled}
              error={getFieldError(FormFields.admitTimestamp)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.chiefComplaint)}
            label="Chief Complaint"
            disabled={disabled}
            error={getFieldError(FormFields.chiefComplaint)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.modeOfArrivalText)}
            label="Mode of Arrival"
            disabled={disabled}
            error={getFieldError(FormFields.modeOfArrivalText)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.dischargedDisposition)}
            label="Discharged Disposition"
            disabled={disabled}
            error={getFieldError(FormFields.dischargedDisposition)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.dischargeLocation)}
            label="Discharge Location"
            disabled={disabled}
            error={getFieldError(FormFields.dischargeLocation)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.patientLocation)}
            label="Patient Location"
            disabled={disabled}
            error={getFieldError(FormFields.patientLocation)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.patientClass)}
            label="Patient Class"
            disabled={disabled}
            error={getFieldError(FormFields.patientClass)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.patientType)}
            label="Patient Type"
            disabled={disabled}
            error={getFieldError(FormFields.patientType)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.hospitalService)}
            label="Hospital Service"
            disabled={disabled}
            error={getFieldError(FormFields.hospitalService)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.servicingFacility)}
            label="Servicing Facility"
            disabled={disabled}
            error={getFieldError(FormFields.servicingFacility)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.accountStatus)}
            label="Account Status"
            disabled={disabled}
            error={getFieldError(FormFields.accountStatus)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.billToProviderId)}
            label="Bill To Provider ID"
            disabled={disabled}
            error={getFieldError(FormFields.billToProviderId)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.billToProviderLastname)}
            label="Bill To Provider Last Name"
            disabled={disabled}
            error={getFieldError(FormFields.billToProviderLastname)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.billToProviderFirstname)}
            label="Bill To Provider First Name"
            disabled={disabled}
            error={getFieldError(FormFields.billToProviderFirstname)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.billtToProviderMiddlename)}
            label="Bill To Provider Middle Name"
            disabled={disabled}
            error={getFieldError(FormFields.billtToProviderMiddlename)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.hasInsurance)}
            label="Contains Insurance Information"
            disabled
          />
          {showAddInsurance && (
            <Box mt={1}>
              <Button
                size="small"
                disabled={disabled}
                onClick={() => onAddInsurance(visitId)}
              >
                Add Insurance Info
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </FormWrapper>
  );
};

Hl7VisitForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAddInsurance: PropTypes.func,
  initValues: PropTypes.shape({}),
  disabled: PropTypes.bool,
  hideButtons: PropTypes.bool,
};

export default Hl7VisitForm;
